// src/pages/LoginPage.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import { UAParser } from 'ua-parser-js';
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate, useSearchParams } from "react-router-dom";

//import backgroundImg from "../assets/images/login/background.jpg";
//import loginIllustration from "../assets/images/login/bg.png";
import loginIllustration from "../assets/images/login/nhotms_1.png";
import logoImage from "../assets/images/login/nhlogo.png";
import { useDispatch } from "react-redux";
import { useApiUtil } from "../utils/apiUtil";
import { useCommon } from "../utils/common";

import {
  setLoginSuccess
} from "../store/userSlice";

import {
  Root,
  Wrapper,
  ImageWrapper,
  Image,
  FormWrapper,
  LogoWrapper,
  LogoImage,
} from "../frame/frameLoginStyles"; // 스타일 컴포넌트 import

const LoginPage = ({ onLoginSuccess }) => {
  const dispatch = useDispatch();
  const [username, setUserInputname] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sytmFlagCd, setSytmFlagCd] = useState("PC");
  const isComposingRef = useRef(false);
  const lastKoreanWarningAtRef = useRef(0);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { gfnCallApi } = useApiUtil();
  const commonJs = useCommon();

  // 허용할 문자 정규식 (예: 영문, 숫자, 기본 특수문자)
  const allowedRegex = /^[a-zA-Z0-9!@#$%^&*()\-_=+\[\]{};:'",.<>/?]*$/;
  const koreanRegex = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;

  const showKoreanInputWarning = () => {
    const now = Date.now();

    if (now - lastKoreanWarningAtRef.current < 800) return;

    lastKoreanWarningAtRef.current = now;
    commonJs.showToastMsg("아이디와 비밀번호에는 한글을 입력할 수 없습니다.", "warning");
  };

  const setAllowedInput = (value, setValue) => {
    if (koreanRegex.test(value)) {
      showKoreanInputWarning();
      setValue(value.replace(/[ㄱ-ㅎㅏ-ㅣ가-힣]/g, ""));
      return;
    }

    if (allowedRegex.test(value)) {
      setValue(value);
    }
  };

  // onChange 핸들러에서 필터링
  const handleUsernameChange = (e) => {
    const value = e.target.value;

    if (isComposingRef.current) {
      setUserInputname(value);
      return;
    }

    setAllowedInput(value, setUserInputname);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;

    if (isComposingRef.current) {
      setPassword(value);
      return;
    }

    setAllowedInput(value, setPassword);
  };

  const getClientInfo = () => {
    const parser = new UAParser();
    const result = parser.getResult();

    return {
      BROWSER_CD: result.browser.name || '',
      BROWSER_VERSION: result.browser.version || '',
      OS_CD: result.os.name || '',
      OS_VERSION: result.os.version || '',
      //deviceType: result.device.type || 'desktop',
      MOB_YN: ((result.device.type === 'mobile' || result.device.type === 'tablet')?"Y":"N"),

      SCREEN_WIDTH: window.screen.width,
      SCREEN_HEIGHT: window.screen.height,

      //windowWidth: window.innerWidth,
      //windowHeight: window.innerHeight,

      //userAgent: navigator.userAgent,
    };
  };  

  const handleLogin = async (e) => {
    e.preventDefault();

    let valid = true;

    if (!username) {
      setUsernameError("아이디를 입력하세요.");
      valid = false;
    } else {
      setUsernameError("");
    }

    if (!password) {
      setPasswordError("비밀번호를 입력하세요.");
      valid = false;
    } else {
      setPasswordError("");
    }

    if (!valid) return;

    let result;
    let loginCompleted = false;
    setLoading(true);
    try {
      const brInfo = getClientInfo();
      result = await gfnCallApi({
        url: "common/login",
        method: "POST",
        data: {
          username: username,
          password: password,
          SYTM_FLAG_CD: sytmFlagCd,
          browserInfo: brInfo,
        },
        showMsg: false,
      });

      if (result?.count > 0 && result?.data) {

        dispatch(
          setLoginSuccess({
            username: result.data.USER_NAME,
            userId: result.data.USER_ID,
            permId: result.data.PERM_ID,
            userInfo: result.data,
            token: result.data.TOKEN,
          })
        );

        localStorage.setItem("ACCESS_TOKEN", result.data.TOKEN);

        if (rememberMe) {
          localStorage.setItem("savedUsername", username);
        } else {
          localStorage.removeItem("savedUsername");
        }

        onLoginSuccess?.();
        loginCompleted = true;
        navigate(searchParams.get("redirect") || "/");
      } else {
        setErrorMsg("아이디 또는 비밀번호가 틀렸습니다.");
      }
    } catch (error) {
      if (error.status === 401) {
        setErrorMsg("아이디 또는 비밀번호가 틀렸습니다.");
      } else {
        setErrorMsg("서버 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);

      if (result?.count > 0 && result?.data && !loginCompleted) {
        // 아이디 저장 처리
        if (rememberMe) {
          localStorage.setItem("savedUsername", username);
          setUserInputname(username);
        } else {
          localStorage.removeItem("savedUsername");
        }

        dispatch(
          setLoginSuccess({
            username: result.data.USER_NAME,
            userId: result.data.USER_ID,
            permId: result.data.PERM_ID,
            userInfo: result.data,
          })
        );

        onLoginSuccess?.();
        navigate(searchParams.get("redirect") || "/");
      }

    }
  };

  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");

    if (savedUsername) {
      setUserInputname(savedUsername);
      setRememberMe(true);
    }
  }, []);  
  /*
  const getKey = async (e) => {
      const result = await gfnCallApi({
        url: "common/key",
        method: "POST",
        data: {
        },
        showMsg: false,
      });
      
      if(result.message === "SUCCESS") {
        setRsaKey(result.data);
      } else {
        setErrorMsg("시스템 오류가 발생하였습니다.\n관리자에게 문의바랍니다.");
      }
      
  };

  useEffect(() => {
    getKey();
  }, []);
  */

  return (
    <Root>
      <Wrapper>
        <ImageWrapper>
          <Image src={loginIllustration} alt="login" />
        </ImageWrapper>

        <FormWrapper>
          <LogoWrapper>
            <LogoImage src={logoImage} alt="logo" />
          </LogoWrapper>

          <form onSubmit={handleLogin} noValidate>
            <TextField
              fullWidth
              required
              variant="outlined"
              placeholder="아이디를 입력하세요."
              value={username}
              onChange={handleUsernameChange}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={(e) => {
                isComposingRef.current = false;
                setAllowedInput(e.target.value, setUserInputname);
              }}              
              margin="normal"
              error={Boolean(usernameError)}
              helperText={usernameError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
                endAdornment: username && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setUserInputname("")}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                    <ClearIcon
                      sx={{
                        fontSize: 16,
                        color: "#9CA3AF",
                        "&:hover": { color: "#374151" },
                      }}
                    />
                    </IconButton>
                  </InputAdornment>
                ),                
                sx: { backgroundColor: "#fff", height: 36, fontSize: "12px", fontWeight: 500, },
              }}
            />

            <TextField
              fullWidth
              required
              type="password"
              variant="outlined"
              placeholder="비밀번호를 입력하세요."
              value={password}
              onChange={handlePasswordChange}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={(e) => {
                isComposingRef.current = false;
                setAllowedInput(e.target.value, setPassword);
              }}              
              margin="normal"
              error={Boolean(passwordError)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: password && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setPassword("")}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                    <ClearIcon
                      sx={{
                        fontSize: 16,
                        color: "#9CA3AF",
                        "&:hover": { color: "#374151" },
                      }}
                    />
                    </IconButton>
                  </InputAdornment>
                ),                
                sx: { backgroundColor: "#fff", height: 36, fontSize: "12px", fontWeight: 500, },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label="아이디 저장"
              sx={{
                mt: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                  fontWeight: 700,
                },
              }}              
            />

            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMsg}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 3, fontWeight: "bold" }}
              disabled={loading}
            >
              {loading ? "로그인 중..." : "로그인"}
            </Button>
          </form>
        </FormWrapper>
      </Wrapper>
    </Root>
  );
};

export default LoginPage;
